import type { FC } from 'react'
import { useMemo } from 'react'
import { EventType } from '@/features/events/shared/types'
import { EditToolContent, FileInfo, ToolHeader } from '../shared'
import { calculateDiffStatsFromStrings, type DiffStats } from '@/features/events/shared/utils'
import { getLanguageFromPath } from '../utils/fileModificationUtils'

interface EditContextRendererProps {
  readonly file_path: string
  readonly old_string: string
  readonly new_string: string
}

interface EditContextData {
  readonly language: string
  readonly stats: DiffStats
}

export const EditContextRenderer: FC<EditContextRendererProps> = ({
  file_path,
  old_string,
  new_string,
}) => {
  const { language, stats } = useEditContextData(file_path, old_string, new_string)

  return (
    <>
      <ToolHeader eventType={EventType.TOOL_PERMISSION_REQUEST} toolName="Edit" />
      <FileInfo filePath={file_path} stats={stats} />

      <EditToolContent
        mode="pre-tool"
        oldString={old_string}
        newString={new_string}
        language={language}
      />
    </>
  )
}

function useEditContextData(
  file_path: string,
  old_string: string,
  new_string: string,
): EditContextData {
  const language = useMemo(() => getLanguageFromPath(file_path), [file_path])

  const stats = useMemo(
    () => calculateDiffStatsFromStrings(old_string, new_string),
    [old_string, new_string],
  )

  return { language, stats }
}
